import { Drawer, Empty, Flex, Segmented, Typography } from 'antd';
import React from 'react';
import { ActivityIndicator } from 'common/components/UI';
import { FILTERS, TabsType } from './model/constants';
import TransactionsList, { TransactionItem } from './TransactionsList';
import { TransactionType } from '../../../../api/dto/transactions';
import TransactionDetails from './TransactionDetails';
import { TransactionDetailsType } from '../../../../types/transactions';
import { useIntl } from 'react-intl';
import { getFilterTypeLabel } from './model/utils';
import { CloseOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

type DrawerTransactionsProps = {
  loading: boolean;
  exportLoading: boolean;
  isOpen?: boolean;
  onClose?: () => unknown;
  onChangeFilter: (activeTab: TabsType) => void;
  onExportPdf: (transactionId?: string, language?: string) => unknown;
  isEmpty: boolean;
  canLoadMore: boolean;
  onLoadMore: () => void;
  activeTransaction?: TransactionDetailsType;
  setActiveTransaction: (transaction?: TransactionDetailsType) => void;
  onDataSelect: (id: string) => void;
  groupedData: TransactionItem[];
  currentMonthSpendingAmount: number;
  currentMonthHoldsAmount: number;
};

const DrawerTransactions: React.FC<DrawerTransactionsProps> = ({
  loading,
  exportLoading,
  isOpen,
  onClose,
  onChangeFilter,
  onExportPdf,
  isEmpty,
  canLoadMore,
  onLoadMore,
  activeTransaction,
  setActiveTransaction,
  onDataSelect,
  groupedData,
  currentMonthSpendingAmount,
  currentMonthHoldsAmount,
}) => {
  const { formatMessage: t } = useIntl();

  return (
    <Drawer
      closeIcon={<CloseOutlined id='btn__transactions-close'/>}
      title={t({ id: 'transactions.title' })}
      open={isOpen}
      extra={loading && <ActivityIndicator fontSize={18} />}
      onClose={onClose}
      width={560}>
      <Segmented
        block
        style={{ marginBottom: 32 }}
        options={Object.values(FILTERS).map((filter) => ({
          value: filter,
          label: <div id={`transaction-label__${filter.toLowerCase()}`}>{getFilterTypeLabel(t, filter)}</div>,
        }))}
        onChange={(value) => onChangeFilter(value as TransactionType)}
      />
      <Flex style={{ marginBottom: 32 }} justify="space-between">
        <Text strong>
          {t(
            { id: 'transactions.spent' },
            { month: t({ id: `calendar.month.${new Date().getMonth()}` }) },
          )}
        </Text>
        <Text>{`${currentMonthSpendingAmount.toFixed(2)} €`}</Text>
      </Flex>
      <Flex vertical>
        {isEmpty && !loading ? (
          <Empty description={t({ id: 'transactions.empty' })} />
        ) : (
          <Flex vertical>
            <Flex vertical gap={4} style={{ marginBottom: 32 }}>
              {currentMonthHoldsAmount > 0 && !loading && (
                <Flex justify="space-between">
                  <Text style={{ fontSize: 16 }}>{t({ id: 'transactions.hold' })}</Text>
                  <Text>{currentMonthHoldsAmount.toFixed(2)} €</Text>
                </Flex>
              )}
            </Flex>
            <TransactionsList
              transactions={groupedData}
              loading={loading}
              canLoadMore={canLoadMore}
              onLoadMore={onLoadMore}
              onDataSelect={onDataSelect}
              // onPayAgain={onPayAgain}
            />
            <TransactionDetails
              loading={exportLoading}
              transaction={activeTransaction}
              onExportPdf={onExportPdf}
              setTransaction={setActiveTransaction}
            />
          </Flex>
        )}
      </Flex>
    </Drawer>
  );
};

export default DrawerTransactions;
